import type Alpine from "alpinejs";

/**
 * Subtle parallax for `[data-parallax]` elements.
 *
 * Each element reads `data-speed` (default 0.05) and translates on Y
 * relative to the distance between its center and the viewport center.
 * Positive speed = element moves slower than scroll (drifts up as you
 * scroll). Negative = drifts down.
 *
 * Single passive scroll listener, rAF-throttled. Skipped entirely when
 * prefers-reduced-motion is set.
 */
export function registerParallax(_A: typeof Alpine) {
  const start = () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const els = Array.from(
      document.querySelectorAll<HTMLElement>("[data-parallax]"),
    );
    if (els.length === 0) return;

    const items = els.map((el) => ({
      el,
      speed: parseFloat(el.getAttribute("data-speed") || "0.05"),
    }));

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      const vCenter = vh / 2;
      for (const { el, speed } of items) {
        const r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) continue;
        const eCenter = r.top + r.height / 2;
        const offset = (eCenter - vCenter) * speed * -1;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
